// frontend/src/pages/Currency/CurrencyList.jsx
import React, { useEffect, useState } from "react";
import { Edit, Trash2, Plus, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./CurrencyList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CurrencyList = () => {
  const { user } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/currency`, {
        headers: getAuthHeaders(),
      });
      setCurrencies(res.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch currencies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this currency?")) {
      try {
        await axios.delete(`${url}/api/currency/${id}`, {
          headers: getAuthHeaders(),
        });
        toast.success("Currency deleted successfully");
        fetchCurrencies();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete currency");
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading currencies...</div>
      </div>
    );
  }

  return (
    <div className="currency-container">
      <div className="header">
        <div>
          <h2>Currency List</h2>
          <p className="info">Showing all currencies in the system</p>
        </div>
        <button onClick={() => navigate("/AddCurrency")} className="add-button">
          <Plus size={16} /> Add Currency
        </button>
      </div>

      <div className="stats-bar">
        <span>Total: {currencies.length} currencies</span>
        <span>Created by: {user?.name || "You"}</span>
      </div>

      <table className="currency-table">
        <thead>
          <tr>
            <th>Currency Name</th>
            <th>Symbol</th>
            <th>Code</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Last Updated</th>
            <th>Status</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currencies.length === 0 ? (
            <tr>
              <td colSpan="8" className="no-data">
                No currencies found. Create your first currency!
              </td>
            </tr>
          ) : (
            currencies.map((currency) => (
              <tr key={currency._id}>
                <td>
                  <div className="currency-name">
                    <strong>{currency.name}</strong>
                  </div>
                </td>
                <td>
                  <span className="currency-symbol">{currency.symbol || "—"}</span>
                </td>
                <td>
                  <span className="currency-code">{currency.code || "—"}</span>
                </td>
                <td>
                  <div className="user-info">
                    <User size={12} />
                    <span>
                      {currency.created_by?.name ||
                       currency.created_by?.email ||
                       "System"}
                    </span>
                  </div>
                </td>
                <td>
                  {new Date(currency.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </td>
                <td>
                  {currency.updated_at
                    ? new Date(currency.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : "Never"
                  }
                </td>
                <td>
                  <span className={`status-badge ${currency.status}`}>
                    {currency.status}
                  </span>
                </td>
                <td className="actions">
                  <button
                    onClick={() => navigate(`/currency/edit/${currency._id}`)}
                    className="edit-btn"
                    title="Edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(currency._id)}
                    className="delete-btn"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CurrencyList;